import styled from "styled-components";
import pluralize from "pluralize";
import { portalColors } from "src/components";
import { customNames } from "src/utils/helpers";

export const ModalBody = styled.div`
    width: 100%;
    max-width: calc(100vw - 32px);
    background: linear-gradient(180deg, var(--calendar-surface, #ffffff) 0%, var(--calendar-surface-secondary, #f7f9fd) 100%);
    color: ${portalColors.text};
    font-family: Manrope, Helvetica, sans-serif;
    overflow: hidden;
`;

export const ModalContent = styled.div`
    padding: 1.15rem 1.6rem 1.35rem;
    border-top: 1px solid ${portalColors.border};

    @media (max-width: 600px) {
        padding: 1.1rem 1.25rem;
    }
`;

export const ModalFooter = styled.div`
    padding: 1rem 1.6rem 1.35rem;
    border-top: 1px solid ${portalColors.border};
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 12px;
    flex-wrap: wrap;

    @media (max-width: 600px) {
        align-items: stretch;
        flex-direction: column;
        padding: 1rem 1.25rem;

        > div {
            width: 100%;
        }

        button,
        a {
            width: 100%;
            max-width: 100%;
        }
    }
`;

export const HelperText = styled.div`
    font-size: 14px;
    color: ${portalColors.mutedText};
    margin-bottom: 12px;
    line-height: 1.4;
`;

export const FormGrid = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) 210px;
    gap: 28px;

    @media (max-width: 760px) {
        grid-template-columns: 1fr;
    }
`;

export const Field = styled.div`
    margin-bottom: 14px;
    min-width: 0;
`;

export const Label = styled.label`
    display: block;
    font-size: 16px;
    font-weight: 700;
    color: ${portalColors.text};
    margin-bottom: 8px;
`;

export const TextInput = styled.input`
    width: 100%;
    height: 46px;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    box-shadow: none;
    padding: 0 12px;
    text-align: start;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 15px;
    box-sizing: border-box;
    color: ${portalColors.text};
    background: ${portalColors.surface};
    ::placeholder {
        text-align: start;
        color: ${portalColors.textSoft};
    }
    :focus {
        outline: none;
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
    }
`;

export const TextArea = styled.textarea`
    width: 100%;
    min-height: 144px;
    resize: none;
    border: 1px solid ${portalColors.borderStrong};
    border-radius: 8px;
    box-shadow: none;
    padding: 12px;
    text-align: start;
    font-family: Manrope, Helvetica, sans-serif;
    font-size: 15px;
    box-sizing: border-box;
    color: ${portalColors.text};
    background: ${portalColors.surface};
    ::placeholder {
        text-align: start;
        color: ${portalColors.textSoft};
    }
    :focus {
        outline: none;
        border-color: ${portalColors.text};
        box-shadow: 0 0 0 4px ${portalColors.blueSoft};
    }
`;

export const VisibleToRow = styled.div`
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
`;

export const UploadArea = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
`;

export const UploadCircle = styled.div`
    width: 200px;
    height: 200px;
    border-radius: 50%;
    border: 1px solid ${portalColors.border};
    box-shadow: none;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${portalColors.textSoft};
    margin-bottom: 10px;

    img {
        width: 100%;
        height: 100%;
        object-fit: cover;
    }
`;

export const ErrorText = styled.div`
    color: #d92727;
    font-size: 11px;
    margin-top: 6px;
`;

export const PlatformPill = styled.img`
    width: 34px;
    height: 34px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
`;

export const ToggleTrack = styled.button`
    width: 34px;
    height: 20px;
    border-radius: 999px;
    border: none;
    background: ${p => p.checked ? "#6ec14d" : "var(--calendar-border-strong, #c7cfdf)"};
    position: relative;
    cursor: pointer;

    &::after {
        content: "";
        width: 14px;
        height: 14px;
        border-radius: 50%;
        background: ${portalColors.surface};
        position: absolute;
        top: 3px;
        left: ${p => p.checked ? "17px" : "3px"};
        transition: left 0.2s ease;
    }
`;

export const getErrorMessage = (error, fallback) => {
    return error.getApiErrorDetails().message || fallback;
};

export const getVisibilityText = (playlist) => {
    if (!playlist.isPlaylistVisible) {
        return "Hidden";
    }
    if (!playlist.isCompanyList) {
        return `${pluralize(customNames.employees)} & ${pluralize(customNames.clients)}`;
    }
    if (playlist.visibleToEmployees && playlist.visibleToClients) {
        return `${pluralize(customNames.employees)} & ${pluralize(customNames.clients)}`;
    }
    if (playlist.visibleToEmployees) {
        return pluralize(customNames.employees);
    }
    if (playlist.visibleToClients) {
        return pluralize(customNames.clients);
    }
    return "None";
};

export const createInitialForm = (playlist) => ({
    name: playlist?.name || "",
    description: playlist?.description || "",
    spotifyUri: playlist?.spotifyUri || "",
    appleMusicUrl: playlist?.appleMusicUrl || "",
    visibleToEmployees: Boolean(playlist?.visibleToEmployees),
    visibleToClients: Boolean(playlist?.visibleToClients),
    imageUrl: playlist?.imageUrl || ""
});
